import React from "react";
import { diffLines, Change } from "diff";
import remend from "remend";
import { cn } from "../../../../../lib/utils";
import { TekkrMarkdown } from "../../../../../components/shared/markdown/tekkr-markdown";

interface InlineDiffViewProps {
    oldText: string;
    newText: string;
    className?: string;
}

const MAX_UNCHANGED_LINES = 3;

interface ProcessedChange {
    type: "added" | "removed" | "unchanged" | "collapsed";
    value: string;
    hiddenLineCount?: number;
}

export function InlineDiffView({ oldText, newText, className }: InlineDiffViewProps) {
    if (oldText === newText) {
        return (
            <div className={cn("text-xs", className)}>
                <div className="rounded bg-muted/30 px-2 py-1.5">
                    <TekkrMarkdown markdown={remend(oldText)} className="text-xs [&>*]:my-0" />
                </div>
            </div>
        );
    }

    const changes = diffLines(oldText, newText);

    const processedChanges = collapseUnchangedSections(changes);

    return (
        <div className={cn("overflow-hidden rounded text-xs", className)}>
            {processedChanges.map((change, i) => (
                <DiffBlock key={i} change={change} />
            ))}
        </div>
    );
}

function collapseUnchangedSections(changes: Change[]): ProcessedChange[] {
    const result: ProcessedChange[] = [];

    for (const change of changes) {
        if (change.added) {
            result.push({ type: "added", value: change.value });
        } else if (change.removed) {
            result.push({ type: "removed", value: change.value });
        } else {
            const lines = change.value.split("\n").filter((line) => line.trim().length > 0);

            if (lines.length <= MAX_UNCHANGED_LINES) {
                result.push({ type: "unchanged", value: change.value });
            } else {
                const firstLine = lines[0];
                const lastLine = lines[lines.length - 1];
                const hiddenCount = lines.length - 2;

                result.push({ type: "unchanged", value: firstLine });
                result.push({ type: "collapsed", value: "", hiddenLineCount: hiddenCount });
                result.push({ type: "unchanged", value: lastLine });
            }
        }
    }

    return result;
}

function DiffBlock({ change }: { change: ProcessedChange }) {
    if (change.type === "collapsed") {
        return (
            <div className="flex items-center gap-2 px-2 py-1">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">{change.hiddenLineCount} unchanged lines</span>
                <div className="h-px flex-1 bg-border" />
            </div>
        );
    }

    if (change.type === "unchanged") {
        return (
            <div className="bg-muted/30 px-2 py-1 pl-6">
                <TekkrMarkdown markdown={remend(change.value.trim())} className="text-xs [&>*]:my-0" />
            </div>
        );
    }

    if (change.type === "added") {
        return (
            <div className="relative bg-confirmation/20 px-2 py-1">
                <span className="absolute left-2 top-1 select-none font-medium text-confirmation">+</span>
                <div className="pl-4">
                    <TekkrMarkdown markdown={remend(change.value.trim())} className="text-xs [&>*]:my-0" />
                </div>
            </div>
        );
    }

    return (
        <div className="relative bg-destructive/20 px-2 py-1">
            <span className="absolute left-2 top-1 select-none font-medium text-destructive">−</span>
            <div className="pl-4">
                <TekkrMarkdown markdown={remend(change.value.trim())} className="text-xs [&>*]:my-0" />
            </div>
        </div>
    );
}
